import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getMeetingByIdApi,
  updateMeetingApi,
  cancelMeetingApi,
  getMeetingJoinTokenApi,
  processTranscriptApi,
  getMeetingTranscriptsApi,
  getMeetingMinutesApi,
} from "../../../../api/api";
import MeetingsDrawer from "./MeetingsDrawer";
import "./MeetingDetails.scss";

// ==================== DATE HELPERS ====================
// Parses "YYYY-MM-DDTHH:MM:SS..." by splitting the string directly —
// never via `new Date(str)` — so the numbers we display are always
// exactly what came back from the API, with zero timezone reinterpretation.
function parseAsIs(dateStr) {
  if (!dateStr) return null;
  const [datePart, timePart = "00:00"] = dateStr.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);
  return { year, month, day, hour, minute };
}

// Builds a Date from those literal numbers (local constructor — no
// timezone math), purely so status comparisons (now vs start/end) work
// using the same wall-clock numbers the API gave us.
function toLocalDate(dateStr) {
  const p = parseAsIs(dateStr);
  if (!p) return null;
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, 0);
}

function formatDateLong(dateStr) {
  if (!dateStr) return "N/A";
  const p = parseAsIs(dateStr);
  if (!p) return "N/A";
  const d = new Date(p.year, p.month - 1, p.day);
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  const p = parseAsIs(dateStr);
  if (!p) return "";
  return `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`;
}

function formatTimeRange(start, end) {
  return `${formatTime(start)} - ${formatTime(end)}`;
}

// ==================== STATUS HELPERS ====================
function getMeetingStatus(meeting) {
  if (!meeting) return "upcoming";
  if (meeting.meetingStatus === "CANCELLED") return "completed";
  if (meeting.meetingStatus === "COMPLETED") return "completed";
  if (meeting.type === "exchange" && !meeting.mursalLivekitEnabled)
    return "outlook";

  const now = new Date();
  const startTime = toLocalDate(meeting.startTimeIso || meeting.startTime);
  const endTime = toLocalDate(meeting.endTimeIso || meeting.endTime);

  // Check if meeting has ended
  if (now > endTime || meeting.meetingStatus === "COMPLETED")
    return "completed";

  // Check if meeting is currently active (between start and end time)
  if (now >= startTime && now <= endTime && meeting.mursalLivekitEnabled)
    return "live";

  // Check if meeting is in the future
  if (now < startTime && meeting.meetingStatus === "SCHEDULED")
    return "upcoming";

  return "upcoming";
}

const STATUS_LABEL = {
  live: "Join Now",
  upcoming: "Upcoming",
  completed: "Completed",
  outlook: "Open in Outlook",
};

const STATUS_BADGE_NOTE = {
  live: "This meeting is happening now.",
  upcoming: "This meeting hasn't started yet.",
  completed: "This meeting has ended.",
  outlook: "Synced from Exchange / Outlook calendar.",
};

// ==================== COMPONENT ====================
function MeetingDetails() {
  const { id, layout } = useParams();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [transcripts, setTranscripts] = useState([]);
  const [transcription, setTranscription] = useState(null);
  const [fullMinutes, setFullMinutes] = useState(null);
  const [momData, setMomData] = useState(null);
  const [isPullingTranscript, setIsPullingTranscript] = useState(false);
  const [isGeneratingMoM, setIsGeneratingMoM] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [loadingMinutes, setLoadingMinutes] = useState(false);
  const [showMomConfirm, setShowMomConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  // Fetch meeting details
  useEffect(() => {
    fetchMeetingDetails();
  }, [id]);

  const fetchMeetingDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getMeetingByIdApi(id);
      if (response.data?.success) {
        const m = response.data.data;
        const transformed = {
          ...m,
          type: m.meetingSource === "MURSAL" ? "mursal" : "exchange",
          organizer: m.organizerEmail || "Unknown",
          date: m.startTime,
          startTime: m.startTime,
          endTime: m.endTime,
          description: m.description || "",
          invite: m.participants?.map((p) => p.email).join(", ") || "",
          roomName: m.livekitRoomName || "",
          waitingRoom: true,
          recording: m.recordingEnabled || false,
          allowGuests: false,
          includeJoinLink: m.mursalLivekitEnabled || false,
          participants: (m.participants || []).map((p) => ({
            name: p.displayName || p.email,
            email: p.email,
            role: p.participantRole,
            status: p.responseStatus?.toLowerCase() || "pending",
          })),
        };
        setMeeting(transformed);

        // Check if transcripts/minutes exist (basic info)
        if (m.transcripts?.length > 0) {
          setTranscription(m.transcripts[0]);
        }
        if (m.minutes?.length > 0) {
          setMomData(m.minutes[0]);
        }
      } else {
        setError("Meeting not found");
      }
    } catch (err) {
      console.error("Failed to fetch meeting:", err);
      setError(err.response?.data?.message || "Failed to load meeting details");
    } finally {
      setLoading(false);
    }
  };

  // Fetch full transcripts
  const fetchTranscripts = async () => {
    setLoadingTranscript(true);
    try {
      const response = await getMeetingTranscriptsApi(meeting.id);
      if (response.data?.success) {
        setTranscripts(response.data.data);
        if (response.data.data.length > 0) {
          setTranscription(response.data.data[0]);
        }
      }
    } catch (err) {
      console.error("Failed to fetch transcripts:", err);
    } finally {
      setLoadingTranscript(false);
    }
  };

  // Fetch full minutes
  const fetchMinutes = async () => {
    setLoadingMinutes(true);
    try {
      const response = await getMeetingMinutesApi(meeting.id);
      if (response.data?.success && response.data.data.length > 0) {
        setFullMinutes(response.data.data[0]);
        setMomData(response.data.data[0]);
      }
    } catch (err) {
      console.error("Failed to fetch minutes:", err);
    } finally {
      setLoadingMinutes(false);
    }
  };

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const status = meeting ? getMeetingStatus(meeting) : "upcoming";
  const isMursal = meeting?.type === "mursal";
  const isAction = status === "live" || status === "outlook";
  const isCompleted = status === "completed";

  async function handleJoinMeeting() {
    try {
      setToast("Fetching join token...");
      const response = await getMeetingJoinTokenApi(meeting.id);
      if (response.data?.success) {
        navigate(`/mursal-meetings/${meeting.id}`);
      }
    } catch (err) {
      setToast("❌ Failed to get join token");
    }
  }

  function handleBack() {
    navigate(`/meetings/${layout}`);
  }

  function handleEdit() {
    setEditDrawerOpen(true);
  }

  async function handleEditSubmit(updatedData) {
    try {
      let startTimeStr, endTimeStr;

      // Check if the drawer sent combined strings (from the updated drawer)
      if (updatedData.startTime && updatedData.startTime.includes("T")) {
        // Already in the correct format from the drawer (YYYY-MM-DDTHH:MM:00.000Z)
        startTimeStr = updatedData.startTime;
        endTimeStr = updatedData.endTime;
      }
      // Check if drawer sent separate date/time fields
      else if (
        updatedData.date &&
        updatedData.startTime &&
        updatedData.endTime
      ) {
        // Match the mobile app's `_asIs()` format exactly: literal
        // date/time with ".000Z" appended as a fixed text suffix.
        startTimeStr = `${updatedData.date}T${updatedData.startTime}:00.000Z`;
        endTimeStr = `${updatedData.date}T${updatedData.endTime}:00.000Z`;
      }
      // If no time fields at all, don't update them
      else {
        startTimeStr = undefined;
        endTimeStr = undefined;
      }

      const payload = {
        title: updatedData.title,
        description: updatedData.description,
        ...(startTimeStr && { startTime: startTimeStr }),
        ...(endTimeStr && { endTime: endTimeStr }),
        recordingEnabled: updatedData.recordingEnabled ?? updatedData.recording,
        transcriptEnabled: updatedData.transcriptEnabled,
        minutesEnabled: updatedData.minutesEnabled,
      };

      console.log("📤 Updating meeting:", payload);

      const response = await updateMeetingApi(meeting.id, payload);
      if (response.data?.success) {
        setToast(`✅ Meeting "${updatedData.title}" updated successfully!`);
        setEditDrawerOpen(false);
        fetchMeetingDetails();
      }
    } catch (err) {
      setToast(
        "❌ " + (err.response?.data?.message || "Failed to update meeting"),
      );
    }
  }
  function handleCancelMeeting() {
    setShowCancelConfirm(true);
  }

  async function confirmCancel() {
    try {
      await cancelMeetingApi(meeting.id);
      setToast(`✅ Meeting "${meeting.title}" has been cancelled.`);
      setShowCancelConfirm(false);
      setTimeout(() => navigate(`/meetings/${layout}`), 1500);
    } catch (err) {
      setToast(
        "❌ " + (err.response?.data?.message || "Failed to cancel meeting"),
      );
      setShowCancelConfirm(false);
    }
  }

  // Pull transcript from server
  async function handlePullTranscript() {
    try {
      // Refresh transcripts and minutes
      await fetchTranscripts();
      await fetchMinutes();
      setActiveTab("transcription");
    } catch (err) {
      setToast(
        "❌ " + (err.response?.data?.message || "Failed to pull transcript"),
      );
    } finally {
      setIsPullingTranscript(false);
    }
  }

  // Generate MoM
  function handleGenerateMoM() {
    if (!transcription) {
      setToast("Please pull the transcript first before generating MoM.");
      return;
    }
    setShowMomConfirm(true);
  }

  async function confirmGenerateMoM() {
    setShowMomConfirm(false);
    setIsGeneratingMoM(true);

    try {
      const response = await processTranscriptApi(meeting.id);
      if (response.data?.success) {
        setToast("✅ Minutes of Meeting generated successfully!");
        await fetchMinutes();
        setActiveTab("mom");
      }
    } catch (err) {
      setToast(
        "❌ " + (err.response?.data?.message || "Failed to generate MoM"),
      );
    } finally {
      setIsGeneratingMoM(false);
    }
  }

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <div
        className="meeting-details"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: 12, color: "#6b7280" }}>
            Loading meeting details...
          </p>
        </div>
      </div>
    );
  }

  // ==================== ERROR STATE ====================
  if (error || !meeting) {
    return (
      <div
        className="meeting-details"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <p style={{ color: "#ef4444", marginBottom: 12 }}>
            ⚠️ {error || "Meeting not found"}
          </p>
          <button className="btn btn-outline-primary" onClick={handleBack}>
            Back to Meetings
          </button>
        </div>
      </div>
    );
  }

  // ==================== RENDER ====================
  return (
    <div className="meeting-details">
      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <div
          className="meeting-details__overlay"
          onClick={() => setShowCancelConfirm(false)}
        >
          <div
            className="meeting-details__drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="meeting-details__drawer-header">
              <div>
                <h2>Cancel Meeting</h2>
                <p className="meeting-details__drawer-subtitle">
                  Are you sure you want to cancel this meeting?
                </p>
              </div>
              <button
                type="button"
                className="meeting-details__drawer-close"
                onClick={() => setShowCancelConfirm(false)}
              >
                ×
              </button>
            </div>
            <div className="meeting-details__drawer-body">
              <p className="meeting-details__cancel-text">
                This will cancel "{meeting.title}" and notify all participants.
              </p>
            </div>
            <div className="meeting-details__drawer-footer">
              <button
                type="button"
                className="meeting-details__btn meeting-details__btn--secondary"
                onClick={() => setShowCancelConfirm(false)}
              >
                Keep Meeting
              </button>
              <button
                type="button"
                className="meeting-details__btn meeting-details__btn--danger"
                onClick={confirmCancel}
              >
                Yes, Cancel Meeting
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MoM Generation Confirmation */}
      {showMomConfirm && (
        <div
          className="meeting-details__overlay"
          onClick={() => setShowMomConfirm(false)}
        >
          <div
            className="meeting-details__drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="meeting-details__drawer-header">
              <div>
                <h2>Generate Minutes of Meeting</h2>
                <p className="meeting-details__drawer-subtitle">
                  AI will analyze the transcript and generate meeting minutes.
                </p>
              </div>
              <button
                type="button"
                className="meeting-details__drawer-close"
                onClick={() => setShowMomConfirm(false)}
              >
                ×
              </button>
            </div>
            <div className="meeting-details__drawer-body">
              <div className="meeting-details__mom-confirm">
                <div className="meeting-details__mom-confirm-item">
                  <span className="meeting-details__mom-confirm-icon">📝</span>
                  <div>
                    <strong>Meeting Summary</strong>
                    <p>Key discussion points and decisions</p>
                  </div>
                </div>
                <div className="meeting-details__mom-confirm-item">
                  <span className="meeting-details__mom-confirm-icon">✅</span>
                  <div>
                    <strong>Action Items</strong>
                    <p>Tasks with assignees and deadlines</p>
                  </div>
                </div>
                <div className="meeting-details__mom-confirm-item">
                  <span className="meeting-details__mom-confirm-icon">📋</span>
                  <div>
                    <strong>Decisions Made</strong>
                    <p>Important decisions taken during meeting</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="meeting-details__drawer-footer">
              <button
                type="button"
                className="meeting-details__btn meeting-details__btn--secondary"
                onClick={() => setShowMomConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="meeting-details__btn meeting-details__btn--primary"
                onClick={confirmGenerateMoM}
              >
                Generate MoM
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Meeting Drawer */}
      {editDrawerOpen && (
        <MeetingsDrawer
          editMode={true}
          meetingData={meeting}
          onClose={() => setEditDrawerOpen(false)}
          onSubmit={handleEditSubmit}
        />
      )}

      {/* Header */}
      <div className="meeting-details__header">
        <button
          type="button"
          className="meeting-details__back-btn"
          onClick={handleBack}
        >
          ‹ Back to Meetings
        </button>

        <div className="meeting-details__title-row">
          <div className="meeting-details__title-block">
            <div className="meeting-details__badges">
              <span
                className={`meeting-details__badge meeting-details__badge--type-${meeting.type}`}
              >
                {isMursal ? "Mursal" : "Exchange"}
              </span>
              {isAction ? (
                <button
                  type="button"
                  className={`meeting-details__badge meeting-details__badge--status-${status} meeting-details__badge--clickable`}
                  onClick={handleJoinMeeting}
                >
                  {STATUS_LABEL[status]}
                </button>
              ) : (
                <span
                  className={`meeting-details__badge meeting-details__badge--status-${status}`}
                >
                  {STATUS_LABEL[status]}
                </span>
              )}
            </div>
            <h1 className="meeting-details__title">{meeting.title}</h1>
            <p className="meeting-details__meta">
              {formatDateLong(meeting.startTime)} &middot;{" "}
              {formatTimeRange(meeting.startTime, meeting.endTime)} &middot;
              Organized by {meeting.organizer}
            </p>
          </div>

          <div className="meeting-details__action-btns">
            <button
              type="button"
              className="meeting-details__btn meeting-details__btn--secondary"
              onClick={handleEdit}
            >
              Edit Meeting
            </button>
            <button
              type="button"
              className="meeting-details__btn meeting-details__btn--danger"
              onClick={handleCancelMeeting}
            >
              Cancel Meeting
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="meeting-details__tabs">
          <button
            type="button"
            className={`meeting-details__tab ${activeTab === "overview" ? "meeting-details__tab--active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            type="button"
            className={`meeting-details__tab ${activeTab === "transcription" ? "meeting-details__tab--active" : ""}`}
            onClick={() => {
              setActiveTab("transcription");
              if (transcripts.length === 0) fetchTranscripts();
            }}
          >
            <span className="meeting-details__tab-icon">📄</span>
            Transcript
            {transcription && (
              <span className="meeting-details__tab-badge meeting-details__tab-badge--success">
                Ready
              </span>
            )}
          </button>
          <button
            type="button"
            className={`meeting-details__tab ${activeTab === "mom" ? "meeting-details__tab--active" : ""}`}
            onClick={() => {
              setActiveTab("mom");
              if (!fullMinutes) fetchMinutes();
            }}
          >
            <span className="meeting-details__tab-icon">📋</span>
            Minutes of Meeting
            {momData && (
              <span className="meeting-details__tab-badge meeting-details__tab-badge--success">
                Ready
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="meeting-details__grid">
          <div className="meeting-details__grid-main">
            <div className="meeting-details__card">
              <h2 className="meeting-details__card-title">Overview</h2>
              <div className="meeting-details__detail-row">
                <span className="meeting-details__detail-label">
                  Meeting Type
                </span>
                <span className="meeting-details__detail-value">
                  {isMursal
                    ? "Mursal Meeting (LiveKit)"
                    : "Exchange Meeting (Outlook)"}
                </span>
              </div>
              <div className="meeting-details__detail-row">
                <span className="meeting-details__detail-label">Date</span>
                <span className="meeting-details__detail-value">
                  {formatDateLong(meeting.startTime)}
                </span>
              </div>
              <div className="meeting-details__detail-row">
                <span className="meeting-details__detail-label">Time</span>
                <span className="meeting-details__detail-value">
                  {formatTimeRange(meeting.startTime, meeting.endTime)}
                </span>
              </div>
              <div className="meeting-details__detail-row">
                <span className="meeting-details__detail-label">Timezone</span>
                <span className="meeting-details__detail-value">
                  {meeting.timezone || "UTC"}
                </span>
              </div>
              <div className="meeting-details__detail-row">
                <span className="meeting-details__detail-label">Organizer</span>
                <span className="meeting-details__detail-value">
                  {meeting.organizer}
                </span>
              </div>
              <div className="meeting-details__detail-row">
                <span className="meeting-details__detail-label">Invited</span>
                <span className="meeting-details__detail-value">
                  {meeting.invite || "—"}
                </span>
              </div>
            </div>

            <div className="meeting-details__card">
              <h2 className="meeting-details__card-title">
                Description / Agenda
              </h2>
              <p className="meeting-details__description">
                {meeting.description || "No description provided."}
              </p>
            </div>

            {isMursal ? (
              <div className="meeting-details__card">
                <h2 className="meeting-details__card-title">
                  Mursal Meeting Settings
                </h2>
                <ul className="meeting-details__settings-list">
                  <li className="meeting-details__settings-item">
                    <span>Recording</span>
                    <span
                      className={`meeting-details__pill ${meeting.recordingEnabled ? "meeting-details__pill--on" : "meeting-details__pill--off"}`}
                    >
                      {meeting.recordingEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </li>
                  <li className="meeting-details__settings-item">
                    <span>Transcript</span>
                    <span
                      className={`meeting-details__pill ${meeting.transcriptEnabled ? "meeting-details__pill--on" : "meeting-details__pill--off"}`}
                    >
                      {meeting.transcriptEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </li>
                  <li className="meeting-details__settings-item">
                    <span>Minutes (MoM)</span>
                    <span
                      className={`meeting-details__pill ${meeting.minutesEnabled ? "meeting-details__pill--on" : "meeting-details__pill--off"}`}
                    >
                      {meeting.minutesEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </li>
                  <li className="meeting-details__settings-item">
                    <span>LiveKit Room</span>
                    <span className="meeting-details__code">
                      {meeting.livekitRoomName || "N/A"}
                    </span>
                  </li>
                </ul>
              </div>
            ) : (
              <div className="meeting-details__card">
                <h2 className="meeting-details__card-title">
                  Exchange Meeting Settings
                </h2>
                <ul className="meeting-details__settings-list">
                  <li className="meeting-details__settings-item">
                    <span>Synced Calendar</span>
                    <span className="meeting-details__pill meeting-details__pill--on">
                      Outlook
                    </span>
                  </li>
                  <li className="meeting-details__settings-item">
                    <span>Mursal Join Link</span>
                    <span
                      className={`meeting-details__pill ${meeting.includeJoinLink ? "meeting-details__pill--on" : "meeting-details__pill--off"}`}
                    >
                      {meeting.includeJoinLink ? "Included" : "Not Included"}
                    </span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div className="meeting-details__grid-side">
            <div className="meeting-details__card meeting-details__card--status">
              <h2 className="meeting-details__card-title">Status</h2>
              <div className="meeting-details__status-block">
                <span
                  className={`meeting-details__badge meeting-details__badge--status-${status} meeting-details__badge--lg`}
                >
                  {STATUS_LABEL[status]}
                </span>
                <p className="meeting-details__status-note">
                  {STATUS_BADGE_NOTE[status]}
                </p>
              </div>
              {isAction && (
                <button
                  type="button"
                  className="meeting-details__btn meeting-details__btn--primary meeting-details__btn--full"
                  onClick={handleJoinMeeting}
                >
                  {status === "live" ? "Join Meeting" : "Open in Outlook"}
                </button>
              )}
            </div>

            {isCompleted && (
              <div className="meeting-details__card">
                <h2 className="meeting-details__card-title">
                  Post-Meeting Actions
                </h2>
                <div className="meeting-details__quick-actions">
                  <button
                    type="button"
                    className="meeting-details__quick-action meeting-details__quick-action--transcript"
                    onClick={handlePullTranscript}
                    disabled={isPullingTranscript}
                  >
                    <span className="meeting-details__quick-action-icon">
                      {isPullingTranscript ? "⏳" : "📄"}
                    </span>
                    <div className="meeting-details__quick-action-content">
                      <span className="meeting-details__quick-action-label">
                        {isPullingTranscript
                          ? "Processing..."
                          : transcription
                            ? "Transcript Ready"
                            : "Process Transcript"}
                      </span>
                      <span className="meeting-details__quick-action-hint">
                        {transcription
                          ? "View or download transcript"
                          : "AI-powered transcription"}
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="meeting-details__quick-action meeting-details__quick-action--mom"
                    onClick={handleGenerateMoM}
                    disabled={isGeneratingMoM || !transcription}
                  >
                    <span className="meeting-details__quick-action-icon">
                      {isGeneratingMoM ? "⏳" : "📋"}
                    </span>
                    <div className="meeting-details__quick-action-content">
                      <span className="meeting-details__quick-action-label">
                        {isGeneratingMoM
                          ? "Generating..."
                          : momData
                            ? "Regenerate MoM"
                            : "Generate MoM"}
                      </span>
                      <span className="meeting-details__quick-action-hint">
                        {!transcription
                          ? "Process transcript first"
                          : "AI-powered minutes"}
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            )}

            <div className="meeting-details__card">
              <h2 className="meeting-details__card-title">
                Participants
                <span className="meeting-details__card-count">
                  {meeting.participants?.length || 0}
                </span>
              </h2>
              <table className="meeting-details__participants-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(meeting.participants || []).map((p, idx) => (
                    <tr key={idx}>
                      <td className="meeting-details__participant-name">
                        {p.name || p.email}
                      </td>
                      <td>{p.role}</td>
                      <td>
                        <span
                          className={`meeting-details__pill meeting-details__pill--${p.status}`}
                        >
                          {p.status?.charAt(0).toUpperCase() +
                            p.status?.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(!meeting.participants ||
                    meeting.participants.length === 0) && (
                    <tr>
                      <td
                        colSpan={3}
                        className="meeting-details__participants-empty"
                      >
                        No participants added.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Transcription Tab */}
      {activeTab === "transcription" && (
        <div
          className="meeting-details__tab-content"
          style={{ padding: "24px" }}
        >
          {loadingTranscript ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: 12, color: "#6b7280" }}>
                Loading transcript...
              </p>
            </div>
          ) : transcripts.length > 0 ? (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              {transcripts.map((t, idx) => (
                <div
                  key={t.id}
                  className="meeting-details__card"
                  style={{ maxWidth: "900px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <div>
                      <h3
                        style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}
                      >
                        Transcript {transcripts.length > 1 ? `#${idx + 1}` : ""}
                      </h3>
                      <div
                        style={{
                          display: "flex",
                          gap: "16px",
                          marginTop: "4px",
                          fontSize: "12px",
                          color: "#6b7280",
                        }}
                      >
                        <span>Model: {t.modelName}</span>
                        <span>
                          Created: {new Date(t.createdAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        className="meeting-details__btn meeting-details__btn--secondary"
                        style={{ fontSize: "12px", padding: "6px 12px" }}
                        onClick={() => {
                          navigator.clipboard.writeText(t.transcriptText || "");
                          setToast("📋 Transcript copied to clipboard!");
                        }}
                      >
                        📋 Copy
                      </button>
                      <button
                        className="meeting-details__btn meeting-details__btn--secondary"
                        style={{ fontSize: "12px", padding: "6px 12px" }}
                        onClick={() => {
                          const blob = new Blob([t.transcriptText || ""], {
                            type: "text/plain",
                          });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `transcript-${meeting.id}-${idx + 1}.txt`;
                          a.click();
                          URL.revokeObjectURL(url);
                          setToast("📥 Transcript downloaded!");
                        }}
                      >
                        ⬇ Download
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
                      background: "#f9fafb",
                      padding: "20px",
                      borderRadius: "8px",
                      whiteSpace: "pre-wrap",
                      fontFamily: "ui-monospace, monospace",
                      fontSize: "13px",
                      lineHeight: "1.6",
                      maxHeight: "600px",
                      overflowY: "auto",
                      color: "#111827",
                    }}
                  >
                    {t.transcriptText || "No transcript text available."}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <p style={{ color: "#6b7280", marginBottom: "16px" }}>
                No transcripts available yet.
              </p>
              <button
                className="meeting-details__btn meeting-details__btn--primary"
                onClick={handlePullTranscript}
                disabled={isPullingTranscript}
              >
                {isPullingTranscript
                  ? "⏳ Processing..."
                  : "📄 Process Transcript"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Minutes of Meeting Tab */}
      {activeTab === "mom" && (
        <div
          className="meeting-details__tab-content"
          style={{ padding: "24px" }}
        >
          {loadingMinutes ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: 12, color: "#6b7280" }}>
                Loading minutes...
              </p>
            </div>
          ) : fullMinutes ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                maxWidth: "900px",
              }}
            >
              {/* Summary */}
              <div className="meeting-details__card">
                <h3
                  style={{
                    margin: "0 0 12px",
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  📝 Executive Summary
                </h3>
                <p style={{ color: "#374151", lineHeight: "1.7", margin: 0 }}>
                  {typeof fullMinutes.summary === "string"
                    ? fullMinutes.summary
                    : fullMinutes.summary?.text ||
                      fullMinutes.summary?.content ||
                      JSON.stringify(fullMinutes.summary) ||
                      "No summary available."}
                </p>
              </div>

              {/* Discussion Points */}
              {fullMinutes.discussion_points?.length > 0 ||
              fullMinutes.discussionPoints?.length > 0 ? (
                <div className="meeting-details__card">
                  <h3
                    style={{
                      margin: "0 0 12px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    💬 Discussion Points
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {(
                      fullMinutes.discussion_points ||
                      fullMinutes.discussionPoints ||
                      []
                    ).map((point, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "12px",
                          background: "#f9fafb",
                          borderRadius: "8px",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        {typeof point === "string" ? (
                          <p
                            style={{
                              margin: 0,
                              color: "#374151",
                              lineHeight: "1.6",
                            }}
                          >
                            {point}
                          </p>
                        ) : (
                          <>
                            {point.topic && (
                              <h4
                                style={{
                                  margin: "0 0 8px",
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#111827",
                                }}
                              >
                                {point.topic}
                              </h4>
                            )}
                            {point.details && (
                              <p
                                style={{
                                  margin: "0 0 8px",
                                  color: "#374151",
                                  lineHeight: "1.6",
                                }}
                              >
                                {point.details}
                              </p>
                            )}
                            {point.speakers &&
                              Array.isArray(point.speakers) &&
                              point.speakers.length > 0 && (
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: "12px",
                                    color: "#6b7280",
                                  }}
                                >
                                  Speakers: {point.speakers.join(", ")}
                                </p>
                              )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Decisions */}
              {fullMinutes.decisions?.length > 0 && (
                <div className="meeting-details__card">
                  <h3
                    style={{
                      margin: "0 0 12px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    ✅ Decisions Made
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {fullMinutes.decisions.map((d, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "12px",
                          background: "#f0fdf4",
                          borderRadius: "8px",
                          border: "1px solid #bbf7d0",
                        }}
                      >
                        {typeof d === "string" ? (
                          <p
                            style={{
                              margin: 0,
                              color: "#166534",
                              lineHeight: "1.6",
                            }}
                          >
                            {d}
                          </p>
                        ) : (
                          <>
                            {d.decision && (
                              <p
                                style={{
                                  margin: "0 0 4px",
                                  fontWeight: 500,
                                  color: "#166534",
                                }}
                              >
                                {d.decision}
                              </p>
                            )}
                            {d.rationale && (
                              <p
                                style={{
                                  margin: "0 0 4px",
                                  fontSize: "13px",
                                  color: "#374151",
                                }}
                              >
                                Rationale: {d.rationale}
                              </p>
                            )}
                            {d.decided_by && (
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "12px",
                                  color: "#6b7280",
                                }}
                              >
                                Decided by: {d.decided_by}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Items */}
              {fullMinutes.action_items?.length > 0 ||
              fullMinutes.actionItems?.length > 0 ? (
                <div className="meeting-details__card">
                  <h3
                    style={{
                      margin: "0 0 12px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    🎯 Action Items
                  </h3>
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "13px",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            borderBottom: "2px solid #e5e7eb",
                            textAlign: "left",
                          }}
                        >
                          <th
                            style={{
                              padding: "10px 12px",
                              fontWeight: 600,
                              color: "#374151",
                            }}
                          >
                            Task
                          </th>
                          <th
                            style={{
                              padding: "10px 12px",
                              fontWeight: 600,
                              color: "#374151",
                            }}
                          >
                            Owner
                          </th>
                          <th
                            style={{
                              padding: "10px 12px",
                              fontWeight: 600,
                              color: "#374151",
                            }}
                          >
                            Due Date
                          </th>
                          <th
                            style={{
                              padding: "10px 12px",
                              fontWeight: 600,
                              color: "#374151",
                            }}
                          >
                            Priority
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(
                          fullMinutes.action_items ||
                          fullMinutes.actionItems ||
                          []
                        ).map((item, idx) => (
                          <tr
                            key={idx}
                            style={{ borderBottom: "1px solid #f3f4f6" }}
                          >
                            <td
                              style={{ padding: "10px 12px", color: "#111827" }}
                            >
                              {typeof item === "string"
                                ? item
                                : item.task || item.action || item.title || "—"}
                            </td>
                            <td
                              style={{ padding: "10px 12px", color: "#6b7280" }}
                            >
                              {typeof item === "string"
                                ? "—"
                                : item.owner ||
                                  item.assignee ||
                                  item.assigned_to ||
                                  "—"}
                            </td>
                            <td
                              style={{ padding: "10px 12px", color: "#6b7280" }}
                            >
                              {typeof item === "string"
                                ? "—"
                                : item.due_date ||
                                  item.deadline ||
                                  item.dueDate ||
                                  "—"}
                            </td>
                            <td style={{ padding: "10px 12px" }}>
                              {typeof item !== "string" &&
                              (item.priority || item.urgency) ? (
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "2px 10px",
                                    borderRadius: "999px",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    background:
                                      (
                                        item.priority || item.urgency
                                      )?.toUpperCase() === "HIGH"
                                        ? "rgba(220, 38, 38, 0.1)"
                                        : (
                                              item.priority || item.urgency
                                            )?.toUpperCase() === "MEDIUM"
                                          ? "rgba(180, 83, 9, 0.1)"
                                          : "rgba(107, 114, 128, 0.1)",
                                    color:
                                      (
                                        item.priority || item.urgency
                                      )?.toUpperCase() === "HIGH"
                                        ? "#dc2626"
                                        : (
                                              item.priority || item.urgency
                                            )?.toUpperCase() === "MEDIUM"
                                          ? "#b45309"
                                          : "#6b7280",
                                  }}
                                >
                                  {item.priority || item.urgency}
                                </span>
                              ) : (
                                <span style={{ color: "#9ca3af" }}>—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {/* Risks */}
              {fullMinutes.risks?.length > 0 && (
                <div className="meeting-details__card">
                  <h3
                    style={{
                      margin: "0 0 12px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    ⚠️ Risks & Mitigations
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {fullMinutes.risks.map((risk, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "12px",
                          background: "#fef2f2",
                          borderRadius: "8px",
                          border: "1px solid #fecaca",
                        }}
                      >
                        {typeof risk === "string" ? (
                          <p
                            style={{
                              margin: 0,
                              color: "#991b1b",
                              lineHeight: "1.6",
                            }}
                          >
                            {risk}
                          </p>
                        ) : (
                          <>
                            {risk.risk && (
                              <p
                                style={{
                                  margin: "0 0 4px",
                                  fontWeight: 500,
                                  color: "#991b1b",
                                }}
                              >
                                {risk.risk}
                              </p>
                            )}
                            {risk.impact && (
                              <p
                                style={{
                                  margin: "0 0 4px",
                                  fontSize: "13px",
                                  color: "#374151",
                                }}
                              >
                                Impact: {risk.impact}
                              </p>
                            )}
                            {risk.probability && (
                              <p
                                style={{
                                  margin: "0 0 4px",
                                  fontSize: "13px",
                                  color: "#374151",
                                }}
                              >
                                Probability: {risk.probability}
                              </p>
                            )}
                            {risk.mitigation && (
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "13px",
                                  color: "#065f46",
                                }}
                              >
                                Mitigation: {risk.mitigation}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Next Steps */}
              {fullMinutes.next_steps?.length > 0 ||
              fullMinutes.nextSteps?.length > 0 ? (
                <div className="meeting-details__card">
                  <h3
                    style={{
                      margin: "0 0 12px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    🔜 Next Steps
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {(
                      fullMinutes.next_steps ||
                      fullMinutes.nextSteps ||
                      []
                    ).map((step, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "12px",
                          background: "#eff6ff",
                          borderRadius: "8px",
                          border: "1px solid #bfdbfe",
                        }}
                      >
                        {typeof step === "string" ? (
                          <p
                            style={{
                              margin: 0,
                              color: "#1e40af",
                              lineHeight: "1.6",
                            }}
                          >
                            {step}
                          </p>
                        ) : (
                          <>
                            {step.action && (
                              <p
                                style={{
                                  margin: "0 0 4px",
                                  fontWeight: 500,
                                  color: "#1e40af",
                                }}
                              >
                                {step.action}
                              </p>
                            )}
                            {step.owner && (
                              <p
                                style={{
                                  margin: "0 0 4px",
                                  fontSize: "13px",
                                  color: "#374151",
                                }}
                              >
                                Owner: {step.owner}
                              </p>
                            )}
                            {step.deadline && (
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "13px",
                                  color: "#6b7280",
                                }}
                              >
                                Due: {step.deadline}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <p style={{ color: "#6b7280", marginBottom: "16px" }}>
                No minutes available yet.
              </p>
              <button
                className="meeting-details__btn meeting-details__btn--primary"
                onClick={handleGenerateMoM}
                disabled={isGeneratingMoM || !transcription}
              >
                {isGeneratingMoM ? "⏳ Generating..." : "📋 Generate MoM"}
              </button>
            </div>
          )}
        </div>
      )}
      {/* Toast */}
      {toast && <div className="meeting-details__toast">{toast}</div>}
    </div>
  );
}

export default MeetingDetails;
